import { Form, Link, useFetcher } from 'react-router'
import { formatDate } from '@trinity/utils'
import { useEffect, useState } from 'react'
import {
  Stack,
  Typography,
  Select,
  MenuItem,
  Button,
  Table,
  Box,
  Chip,
  Divider,
  Dialog,
  TextField,
} from '../../components'
import { useGlobalState } from '../../hooks'
import { ImageNotFound } from '../../assets'

interface FabricNotificationsProps {
  notifications: TrinityAPI.FabricNotificationType[]
}
export function FabricNotifications({ notifications }: FabricNotificationsProps) {
  const [scope, setScope] = useState<ScopeType>(ALL)

  const filtered = sortNotifications(filterNotifications({ notifications, scope }))

  return (
    <Stack spacing={6}>
      <Typography variant='h4'>Fabric Notifications</Typography>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Select
          value={scope}
          size='small'
          onChange={e => setScope(e.target.value)}
          sx={{ width: { mobile: 150, laptop: 200 } }}
        >
          <MenuItem value={ALL}>All</MenuItem>
          <MenuItem value={NOTIFIED}>Notified</MenuItem>
          <MenuItem value={NOT_NOTIFIED}>Not Notified</MenuItem>
          <MenuItem value={NOT_AVAILABLE}>Unavailable</MenuItem>
        </Select>
        <Form method='post'>
          <input type='hidden' name='intent' value='unsubscribe_all' />
          <input type='hidden' name='ids' value={filtered.map(n => n.id).join(',')} />
          <Button
            type='submit'
            size='small'
            color='neutral'
            variant='outlined'
            title={`Unsubscribe from all${filtered.length === notifications.length ? '' : ' filtered'} notifications`}
            disabled={!filtered.length}
          >
            Unsubscribe All
          </Button>
        </Form>
      </Stack>
      <FabricNotificationsTable notifications={filtered} />
    </Stack>
  )
}

interface FabricNotificationsTableProps {
  notifications: TrinityAPI.FabricNotificationType[]
}

function FabricNotificationsTable({ notifications }: FabricNotificationsTableProps) {
  const { onMobile } = useGlobalState()

  return (
    <Table aria-label='Fabric Notifications' cellBorders={false} spacing={0}>
      {!onMobile && (
        <Table.Head>
          <Table.Row>
            <Table.Cell>Fabric</Table.Cell>
            <Table.Cell>Status</Table.Cell>
            <Table.Cell>Email</Table.Cell>
            <Table.Cell>Notes</Table.Cell>
            <Table.Cell>Subscribed On</Table.Cell>
            <Table.Cell>Notified On</Table.Cell>
            <Table.Cell />
          </Table.Row>
        </Table.Head>
      )}
      <Table.Body>
        {notifications.map(notification => {
          const chipProps = notificationChipProps(notification.fabric.availability)
          return (
            <Table.Row
              key={notification.id}
              isAccordion={onMobile}
              ExpandedComponent={<NotificationExtension notification={notification} />}
            >
              <Table.Cell sx={{ pl: 0 }}>
                <FabricSwatch notification={notification} gap={1} />
              </Table.Cell>
              <Table.Cell>
                <Chip
                  size='small'
                  label={<Typography variant='label'>{chipProps.label}</Typography>}
                  color={chipProps.color}
                  sx={{ width: 'fit-content' }}
                />
              </Table.Cell>
              {!onMobile && (
                <>
                  <Table.Cell>
                    <Typography variant='body1'>{notification.email}</Typography>
                  </Table.Cell>
                  <Table.Cell>
                    <Typography
                      variant='body1'
                      sx={{ maxWidth: '350px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                      title={notification?.notes ?? ''}
                    >
                      {notification.notes}
                    </Typography>
                  </Table.Cell>
                  <Table.Cell>
                    <Typography variant='body1'>{formatDate(new Date(notification.createdAt))}</Typography>
                  </Table.Cell>
                  <Table.Cell>
                    <Typography variant='body1'>
                      {notification.notifiedAt ? formatDate(new Date(notification.notifiedAt)) : '—'}
                    </Typography>
                  </Table.Cell>
                  <Table.Cell align='right'>
                    <FabricNotificationEditDialog notification={notification} />
                  </Table.Cell>
                </>
              )}
            </Table.Row>
          )
        })}
        {!notifications.length && (
          <Table.Row>
            <Table.Cell colSpan={!onMobile ? 5 : 1}>
              <Typography variant='subtitle1' align='center'>
                No fabric notifications found
              </Typography>
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table>
  )
}

interface FabricSwatchProps {
  notification: TrinityAPI.FabricNotificationType
  gap?: number
}

function FabricSwatch({ notification, gap = undefined }: FabricSwatchProps) {
  return (
    <Box sx={{ width: 'fit-content', display: 'flex', flexDirection: 'row', alignItems: 'center', gap }}>
      <img
        width={66}
        height={65}
        src={notification.fabric.url ?? ImageNotFound}
        alt={notification.fabric.trinityFabricNumber}
        onError={e => (e.currentTarget.src = ImageNotFound)}
      />
      <Typography variant='h6' component={Link} to={`/fabric-explorer/fabrics/${notification.fabric.id}`}>
        {notification.fabric.trinityFabricNumber}{' '}
      </Typography>
    </Box>
  )
}

interface FabricNotificationProps {
  notification: TrinityAPI.FabricNotificationType
}

function NotificationExtension({ notification }: FabricNotificationProps) {
  return (
    <Stack spacing={2} sx={{ px: 2, pb: 2 }}>
      <Stack spacing={0.5} sx={{ pt: 1 }}>
        <Typography variant='smallBody2'>Email</Typography>
        <Typography variant='body1'>{notification.email}</Typography>
      </Stack>
      <Stack spacing={0.5} sx={{ pt: 1 }}>
        <Typography variant='smallBody2'>Notes</Typography>
        <Typography variant='body1'>{notification.notes}</Typography>
      </Stack>
      <Stack spacing={0.5} sx={{ pt: 1 }}>
        <Typography variant='smallBody2'>Subscribed On</Typography>
        <Typography variant='body1'>{formatDate(new Date(notification.createdAt))}</Typography>
      </Stack>
      <Stack spacing={0.5}>
        <Typography variant='smallBody2'>Notified On</Typography>
        <Typography variant='body1'>
          {notification.notifiedAt ? formatDate(new Date(notification.notifiedAt)) : '—'}
        </Typography>
      </Stack>
      <Divider />
      <FabricNotificationEditDialog notification={notification} />
    </Stack>
  )
}

function FabricNotificationEditDialog({ notification }: FabricNotificationProps) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button size='small' color='neutral' variant='outlined' onClick={() => setOpen(true)}>
        Edit
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <Stack direction='column' p={2} spacing={2}>
          <Typography variant='h5'>Edit Notification for {notification.fabric.trinityFabricNumber}</Typography>
          <FabricNotificationsForm notification={notification} onCancel={() => setOpen(false)} />
        </Stack>
      </Dialog>
    </>
  )
}

interface FabricNotificationFormProps {
  notification: TrinityAPI.FabricNotificationType | undefined
  onCancel?: () => void
}
export function FabricNotificationsForm({ notification, onCancel = () => null }: FabricNotificationFormProps) {
  const fetcher = useFetcher()
  const { setToastInfo } = useGlobalState()

  useEffect(() => {
    if (fetcher.data) {
      setToastInfo({
        message: fetcher.data.message,
        severity: fetcher.data.error ? 'error' : 'success',
        show: true,
      })
    }
  }, [fetcher?.data, setToastInfo])

  return (
    <fetcher.Form method='post' action='/fabric-explorer/notifications'>
      <Stack gap={2}>
        <Stack spacing={2}>
          <TextField
            required
            name='email'
            type='email'
            label='Email'
            error={Boolean(fetcher.data?.fields?.email)}
            helperText={fetcher.data?.fields?.email}
            defaultValue={notification?.email}
            sx={{ backgroundColor: 'white' }}
          />
          <TextField
            multiline
            rows={4}
            name='notes'
            type='text'
            defaultValue={notification?.notes}
            label='Note'
            sx={{ backgroundColor: 'white' }}
          />
        </Stack>
        <Stack direction={{ mobile: 'column', laptop: 'row' }} gap={2}>
          {!notification ? (
            <>
              <Button type='submit' name='intent' value='createNotification' disabled={fetcher.state !== 'idle'}>
                Notify Me
              </Button>
            </>
          ) : (
            <>
              <input type='hidden' name='notificationId' value={notification.id} />
              <Button type='submit' name='intent' value='updateNotification' disabled={fetcher.state !== 'idle'}>
                Update Notification
              </Button>
              {notification?.notifiedAt && (
                <>
                  <Typography variant='body2'>We notified you on {formatDate(notification.notifiedAt)}</Typography>
                  <Button
                    type='submit'
                    name='intent'
                    value='resubscribeNotification'
                    sx={{ bgcolor: 'primary.light' }}
                    disabled={fetcher.state !== 'idle'}
                  >
                    Resubscribe
                  </Button>
                </>
              )}
              <Button
                type='submit'
                name='intent'
                value='deleteNotification'
                color='secondary'
                variant='outlined'
                disabled={fetcher.state !== 'idle'}
              >
                Unsubscribe
              </Button>
            </>
          )}
          <Button
            variant='outlined'
            color='neutral'
            onClick={() => onCancel()}
            sx={{ marginLeft: { laptop: 'auto !important' } }}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    </fetcher.Form>
  )
}

/** Helpers */
const notificationChipProps = (availability: TrinityAPI.FabricNotificationType['fabric']['availability']) => {
  if (availability?.inStock) return { label: 'In Stock', color: 'success' as const }
  if (!availability) return { label: 'Not Available', color: 'error' as const }
  return { label: 'Temp Out', color: 'warning' as const }
}

const ALL = 'all'
const NOTIFIED = 'notified'
const NOT_NOTIFIED = 'not_notified'
const NOT_AVAILABLE = 'not_available'

type ScopeType = typeof ALL | typeof NOTIFIED | typeof NOT_NOTIFIED | typeof NOT_AVAILABLE

interface FilterNotificationsProps {
  notifications: TrinityAPI.FabricNotificationType[]
  scope: ScopeType
}

const filterNotifications = ({ notifications, scope }: FilterNotificationsProps) => {
  switch (scope) {
    case NOTIFIED:
      return notifications.filter(n => n.notifiedAt)
    case NOT_NOTIFIED:
      return notifications.filter(n => !n.notifiedAt)
    case NOT_AVAILABLE:
      return notifications.filter(n => !n.fabric.availability)
    default:
      return notifications
  }
}

/**
 * Sort notifications with not notified first, followed by unavailable
 * @param notifications
 * @returns
 */
const sortNotifications = (notifications: TrinityAPI.FabricNotificationType[]) =>
  notifications.sort((a, _) => (a.notifiedAt ? (!a.fabric.availability?.inStock ? -1 : 1) : -1))
